<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Checkbox from 'primevue/checkbox';
import ProgressSpinner from 'primevue/progressspinner';
import { CustomButton, ButtonType } from '@/components';
import { DELETE_CONFIRMATION_TYPE } from '@/models';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  isLoading: {
    type: Boolean
  },
  confirmationType: {
    type: String,
    default: DELETE_CONFIRMATION_TYPE.FORM
  }
});

const forForm = props.confirmationType === DELETE_CONFIRMATION_TYPE.FORM;

const strings = {
  header: forForm ? 'Confirm delete forms' : 'Confirm delete data',
  description: forForm ?
    "You're about to permanently delete forms." :
    "You're about to permanently delete data.",
  alertAck: forForm ?
    'Please acknowledge what will happen:' :
    'Are you ok with what will happen to your data?',
  check1: forForm ?
    'Entries created with this form will be deleted immediately' :
    '1. It will be deleted immediately',
  check2: forForm ?
    'This deletion is permanent, there is no undo' :
    '2. It will be deleted permanently and from all backups',
  check3: forForm ?
    'The form and data will no longer exist in Studypages Data' :
    '3. There is no undo, the data will no longer exist in Studypages Data',
  loaderText: forForm ? 'Deleting forms...' : 'Deleting data...'
};

const checkFirst = ref<boolean>(false);
const checkSecond = ref<boolean>(false);
const checkThird = ref<boolean>(false);

const enableDelete = computed(
  () => checkFirst.value && checkSecond.value && checkThird.value
);

const checkChange = (check: number) => {
  if (check === 1) {
    checkFirst.value = !checkFirst.value;
  } else if (check === 2) {
    checkSecond.value = !checkSecond.value;
  } else if (check === 3) {
    checkThird.value = !checkThird.value;
  }
};

const emits = defineEmits(['onClose', 'onDelete']);

const closeDialog = () => {
  emits('onClose');
};

const deleteForms = () => {
  emits('onDelete');
};
</script>

<template>
  <div>
    <Dialog :visible="visible"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <template #container="{}">
        <div class="content-root">
          <div>
            <h3>{{ strings.header }}</h3>
          </div>
          <div class="description">{{ strings.description }}</div>

          <div class="alert-ack">{{ strings.description }}</div>
          <div class="select" @click.prevent="checkChange(1)" v-ripple>
            <div>
              {{ strings.check1 }}
            </div>
            <Checkbox v-model="checkFirst" :binary="true" />
          </div>
          <div class="select" @click.prevent="checkChange(2)" v-ripple>
            <div>{{ strings.check2 }}</div>
            <Checkbox v-model="checkSecond" :binary="true" />
          </div>
          <div class="select" @click.prevent="checkChange(3)" v-ripple>
            <div>{{ strings.check3 }}</div>
            <Checkbox v-model="checkThird" :binary="true" />
          </div>
          <div class="alert-buttons">
            <div class="buttons-left">
              <CustomButton text="Delete permanently"
                :disabled="!enableDelete"
                :type="ButtonType.NEGATIVE_OUTLINED"
                @onClick="deleteForms"/>
              <CustomButton text="Cancel"
                :type="ButtonType.OUTLINED_PRIMARY"
                @onClick="closeDialog"
                :marginLeft="16"/>
            </div>
          </div>
        </div>
        <div class="content-loading" v-if="isLoading">
          <ProgressSpinner strokeWidth="4" />
          <div>{{ strings.loaderText }}</div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "../../assets/main.scss";
@import "../../assets/dialog";
.content-root {
  width: 600px;
  padding: 40px 28px;
}
h3 {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  margin-bottom: 8px;
  padding: 0 12px;
}
.description {
  padding: 10px 12px;
  font-weight: 600;
}
.alert-ack {
  margin-top: 40px;
  padding: 10px 12px;
  font-weight: 600;
}
.select {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: $hover-background;
  }
}
.alert-buttons {
  margin-top: 32px;
  padding: 0 12px;
  display: flex;
  justify-content: flex-end;
}
.buttons-left {
  display: flex;
}
.content-loading {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
